import { CustomImage, CustomTable, useText, Text } from '@src/components';
import React, { useState, useEffect, useMemo } from 'react';
import '@src/styles/companyInformation/deliveryAdresses/deliveryAdressesList.scss';
import { Button, Dropdown, Menu } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import iconFilterNew from '@src/assets/icons/icon-filter-new.svg';
import { DownCircleOutlined, UpCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { postLoadBalancesReport } from '@src/store/slice/balanceUploadDetailSlice';
import FilterMenu from './FilterMenu';
import balanceUploadDetailServices from '@src/services/balanceUploadDetail.services';
import download from 'js-file-download';
import dayjs from 'dayjs';
import { getFileNameByLanguage } from '@src/utils/utils';
import PropTypes from 'prop-types';
import { VouIdCode } from '@src/store/enums';

function getExpandIcon() {
  return ({ expanded, onExpand, record }) =>
    expanded ? (
      <UpCircleOutlined style={{ fontSize: '24px' }} onClick={(e) => onExpand(record, e)} />
    ) : (
      <DownCircleOutlined style={{ fontSize: '24px' }} onClick={(e) => onExpand(record, e)} />
    );
}

const UploadedBalanceReportList = ({ isCompliments = false, isMarket = false }) => {
  const [expandedRows, setExpandedRows] = useState([]);
  const [visibleFilter, setVisibleFilter] = useState(false);
  const [address, setAddress] = useState();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [refNo, setRefNo] = useState(null);
  const [cardNumber, setCardNumber] = useState(null);
  const [nameSurname, setNameSurname] = useState(null);
  const [deliveryId, setDeliveryId] = useState([]);
  const [orderNumber, setOrderNumber] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState(null);
  const [checkedList, setCheckedList] = useState([]);
  const [filteredObject, setFilteredObject] = useState(null);
  const { selectedCustomer } = useSelector((state) => state?.customer);
  const { balancesReportList, addressList, tableProperty } = useSelector(
    (state) => state?.balanceUploadDetail,
  );
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const vouId = useMemo(() => {
    if (isMarket) return VouIdCode.Market;
    if (isCompliments) return VouIdCode.Compliments;
    return VouIdCode.Restaurant;
  }, [isMarket, isCompliments]);

  const segmentId = useMemo(() => {
    if (isMarket) return 1100;
    if (isCompliments) return 9400;
    return 1000;
  }, [isMarket, isCompliments]);

  const dispatch = useDispatch();

  useEffect(async () => {
    let body = {
      customerId: selectedCustomer?.customerId,
      segmentId: segmentId,
      refNo: null,
      cardNumber: null,
      nameSurname: null,
      addressIdList: null,
      orderNumber: null,
      invoiceNumber: null,
      startDate: null,
      endDate: null,
      page: pagination.current,
      pageSize: pagination.pageSize,
      vouId: vouId,
    };
    if (filteredObject) {
      body.refNo = filteredObject?.refNo ?? body.refNo;
      body.cardNumber = filteredObject?.cardNumber ?? body.cardNumber;
      body.nameSurname = filteredObject?.nameSurname ?? body.nameSurname;
      body.addressIdList = filteredObject?.addressIdList ?? body.addressIdList;
      body.orderNumber = filteredObject?.orderNumber ?? body.orderNumber;
      body.invoiceNumber = filteredObject?.invoiceNumber ?? body.invoiceNumber;
      body.startDate = filteredObject?.startDate ?? body.startDate;
      body.endDate = filteredObject?.endDate ?? body.endDate;
    }
    await dispatch(postLoadBalancesReport(body));
  }, [dispatch, pagination]);

  useEffect(() => {
    try {
      if (addressList) {
        setAddress(
          Object.entries(addressList).map(([key, value]) => ({
            key: Number(key),
            value: value,
          })),
        );
      }
    } catch (error) {
      console.error(error);
    }
  }, [addressList]);

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'receiverName',
      width: 20,
      align: 'center',
      render: (text, record, index) => <span>{index + 1}</span>,
    },
    {
      title: (
        <div style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
          <Text t="action" />
          <br /> <Text t="date2" />
        </div>
      ),
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 70,
      align: 'center',
      sorter: (a, b) => {
        const [dayA, monthA, yearA] = a.orderDate.split('/').map(Number);
        const [dayB, monthB, yearB] = b.orderDate.split('/').map(Number);
        return new Date(yearA, monthA - 1, dayA) - new Date(yearB, monthB - 1, dayB);
      },
    },
    {
      title: (
        <div style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
          <Text t="cardNumber" />
        </div>
      ),
      dataIndex: 'cardNumber',
      key: 'cardNumber',
      width: 70,
      align: 'center',
      sorter: (a, b) => a.cardNumber.localeCompare(b.cardNumber),
    },
    {
      title: (
        <div>
          <Text t="nameUpperCase" /> <br /> <Text t="surnameUpperCase" />
        </div>
      ),
      dataIndex: 'nameSurName',
      key: 'nameSurName',
      width: 70,
      align: 'center',
      sorter: (a, b) => a.nameSurName.localeCompare(b.nameSurName),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: (
        <div>
          <Text t="referenceCode3" />
        </div>
      ),
      dataIndex: 'refNo',
      key: 'refNo',
      width: 70,
      align: 'center',
      sorter: (a, b) => a.refNo.localeCompare(b.refNo),
    },
    {
      title: (
        <div>
          <Text t="loaded" /> <br /> <Text t="balance" />
        </div>
      ),
      dataIndex: 'loadAmount',
      key: 'loadAmount',
      width: 70,
      align: 'center',
      responsive: ['xl'],
      render: (text) => <span>{text + ' ₺'}</span>,
    },
    {
      title: (
        <div>
          <Text t="balanceActivation" /> <br /> <Text t="date2" />
        </div>
      ),
      dataIndex: 'balanceActiveDate',
      key: 'balanceActiveDate',
      width: 110,
      align: 'center',
      responsive: ['xl'],
      sorter: (a, b) => {
        const [dayA, monthA, yearA] = a.balanceActiveDate.split('/').map(Number);
        const [dayB, monthB, yearB] = b.balanceActiveDate.split('/').map(Number);
        return new Date(yearA, monthA - 1, dayA) - new Date(yearB, monthB - 1, dayB);
      },
    },
    {
      title: (
        <div>
          <Text t="delivery" /> <br /> <Text t="address3" />
        </div>
      ),
      dataIndex: 'deliveryName',
      key: 'deliveryName',
      width: 80,
      align: 'center',
      sorter: (a, b) => a.deliveryName.localeCompare(b.deliveryName),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: (
        <div>
          <Text t="order2" /> <br />
          NO
        </div>
      ),
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      align: 'center',
      width: 70,
    },
    {
      title: (
        <div>
          <Text t="invoice2" /> <br /> NO
        </div>
      ),
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      align: 'center',
      width: 70,
    },
  ];
  const getPDFFile = async () => {
    const checkeds = checkedList.map((checked) => ({ addressId: checked }));
    const data = await balanceUploadDetailServices.loadBalancesReportGetPDFFile({
      customerId: selectedCustomer?.customerId,
      segmentId: segmentId,
      refNo,
      cardNumber,
      nameSurname,
      orderNumber,
      invoiceNumber,
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      addressIdList: checkeds.length < 1 ? null : checkeds,
      vouId: vouId,
    });
    download(
      data,
      `${getFileNameByLanguage(
        'yüklenen-bakiye-raporu',
        'uploaded-balance-report',
      )}-${dayjs().format('DD-MM-YYYY hh:mm')}.pdf`,
    );
  };

  const getExcelFile = async () => {
    const checkeds = checkedList.map((checked) => ({ addressId: checked }));
    const data = await balanceUploadDetailServices.loadBalancesReportGetExcelFile({
      customerId: selectedCustomer?.customerId,
      segmentId: segmentId,
      refNo,
      cardNumber,
      nameSurname,
      orderNumber,
      invoiceNumber,
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      addressIdList: checkeds.length < 1 ? null : checkeds,
      vouId: vouId,
    });
    download(
      data,
      `${getFileNameByLanguage(
        'yüklenen-bakiye-raporu',
        'uploaded-balance-report',
      )}-${dayjs().format('DD-MM-YYYY hh:mm')}.xlsx`,
    );
  };
  const DownloadMenu = (
    <Menu>
      <Menu.Item onClick={getExcelFile}>
        <Text t="downloadReportExcel" />{' '}
      </Menu.Item>
      <Menu.Item onClick={getPDFFile}>
        <Text t="downloadReportPdf" />
      </Menu.Item>
    </Menu>
  );

  const handleFilterMenuClick = () => {
    setVisibleFilter(false);
  };

  const handlePageChange = (page, pageSize) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      current: page?.current,
    }));
  };

  return (
    <div className="user-list-card">
      <div
        style={{
          fontFamily: 'UbuntuMedium',
          fontSize: '18px',
          color: '#050505',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginLeft: '24px',
          marginRight: '16px',
          paddingTop: '15px',
          paddingBottom: '15px',
        }}
      >
        <div>
          <Text t="loadedBalanceReport" />
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Dropdown
            overlay={
              <FilterMenu
                addressList={address}
                handleFilterMenuClick={handleFilterMenuClick}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                refNo={refNo}
                setRefNo={setRefNo}
                cardNumber={cardNumber}
                setCardNumber={setCardNumber}
                nameSurname={nameSurname}
                setNameSurname={setNameSurname}
                deliveryId={deliveryId}
                setDeliveryId={setDeliveryId}
                orderNumber={orderNumber}
                setOrderNumber={setOrderNumber}
                invoiceNumber={invoiceNumber}
                setInvoiceNumber={setInvoiceNumber}
                checkedList={checkedList}
                setCheckedList={setCheckedList}
                segmentId={segmentId}
                vouId={vouId}
                setFilteredObject={setFilteredObject}
              />
            }
            trigger={['click']}
            visible={visibleFilter}
            onVisibleChange={setVisibleFilter}
          >
            <Button
              type="text"
              icon={<CustomImage src={iconFilterNew} />}
              style={{
                borderRadius: '25%',
                padding: '10px',
                borderColor: '#f1f5f9',
                backgroundColor: ' #f2f5f7',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'auto',
                height: 'auto',
              }}
            />
          </Dropdown>
          <Dropdown overlay={DownloadMenu} trigger={['click']}>
            <Button
              style={{
                borderRadius: '25%',
                padding: '10px',
                borderColor: '#f1f5f9',
                backgroundColor: ' #f2f5f7',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'auto',
                height: 'auto',
              }}
              shape="circle"
              icon={<MoreOutlined />}
            />
          </Dropdown>
        </div>
      </div>
      <CustomTable
        expandedRowKeys={expandedRows}
        expandable={{
          showExpandColumn: false,
          expandIconColumnIndex: 11,
          onExpand: (expanded, record, key) =>
            expandedRows === key ? setExpandedRows(null) : setExpandedRows(key),
          expandIcon: getExpandIcon(),
        }}
        pagination={
          tableProperty?.totalItems < 11
            ? false
            : {
                pageSize: tableProperty?.pageSize,
                total: tableProperty?.totalItems,
                current: tableProperty?.currentPage,
                className: 'demands-list-pagination',
              }
        }
        onPageChange={handlePageChange}
        dataSource={balancesReportList}
        columns={columns}
        className="user-list-table"
        rowKey={(record) => record?.id}
      />
    </div>
  );
};
UploadedBalanceReportList.propTypes = {
  isCompliments: PropTypes.bool,
  isMarket: PropTypes.bool,
};

export default UploadedBalanceReportList;

getExpandIcon.propTypes = {
  expanded: PropTypes.bool,
  onExpand: PropTypes.func,
  record: PropTypes.object,
};
